// contexts/AuthContext.js
import React, { createContext, useState, useContext, useEffect } from "react";
import api from "../services/api";

const AuthContext = createContext(null);

export const useAuth = () => {
  return useContext(AuthContext);
};

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [adminData, setAdminData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem("adminToken");
    if (token) {
      // Token will be handled by api interceptors
      validateToken();
    } else {
      setLoading(false);
    }
  }, []);

  const validateToken = async () => {
    try {
      const response = await api.get("/auth/me");
      if (response.data.success) {
        setIsAuthenticated(true);
        setIsAdmin(true);
        setAdminData(response.data.admin);
        setAuthError(null);
      } else {
        throw new Error("Invalid response");
      }
    } catch (error) {
      console.error("Token validation failed:", error);
      localStorage.removeItem("adminToken");
      // Auth headers will be cleared by api interceptors
      setIsAuthenticated(false);
      setIsAdmin(false);
      setAdminData(null);
      setAuthError("Session expired. Please login again.");
    } finally {
      setLoading(false);
    }
  };

  const login = async (username, password) => {
    try {
      const response = await api.post("/auth/login", {
        username,
        password,
      });

      if (response.data.success) {
        const { token, admin } = response.data;
        localStorage.setItem("adminToken", token);
        setIsAuthenticated(true);
        setIsAdmin(true);
        setAdminData(admin);
        setAuthError(null);
        return { success: true };
      } else {
        return {
          success: false,
          error: response.data.error || "Login failed",
        };
      }
    } catch (error) {
      let errorMessage = "An unexpected error occurred";

      if (error.response?.status === 500) {
        errorMessage =
          "Server is currently unavailable. Please try again later.";
      } else if (error.response?.status === 401) {
        errorMessage = "Invalid username or password";
      } else if (error.request) {
        errorMessage = "Network error. Please check your connection.";
      }

      return {
        success: false,
        error: errorMessage,
      };
    }
  };
  const logout = () => {
    localStorage.removeItem("adminToken");
    // Auth headers will be cleared by api interceptors on next request
    setIsAuthenticated(false);
    setIsAdmin(false);
    setAdminData(null);
    setAuthError(null);
  };

  const value = {
    isAuthenticated,
    isAdmin,
    adminData,
    loading,
    authError,
    login,
    logout,
    validateToken,
    setAuthError,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
